import { ReactNode } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { AlertTriangle, CheckCircle2 } from "lucide-react"
import { cn } from "@/lib/utils"

interface ApprovalCardProps {
  title: string
  description?: string
  variant?: "info" | "warning"
  primaryLabel: string
  onPrimary: () => void | Promise<void>
  secondaryLabel?: string
  onSecondary?: () => void | Promise<void>
  /** Optional "skip step" action — rendered as a ghost button on the right. */
  onSkip?: () => void | Promise<void>
  skipLabel?: string
  skipLoading?: boolean
  /** Optional "go back" action — rendered as a ghost button on the right,
   *  before the skip. Recua o pipeline para o passo anterior. */
  onBack?: () => void | Promise<void>
  backLabel?: string
  backLoading?: boolean
  loading?: boolean
  details?: ReactNode
}

export function ApprovalCard({
  title,
  description,
  variant = "warning",
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  onSkip,
  skipLabel = "Saltar passo →",
  skipLoading,
  onBack,
  backLabel = "← Passo anterior",
  backLoading,
  loading,
  details,
}: ApprovalCardProps) {
  const Icon = variant === "warning" ? AlertTriangle : CheckCircle2
  return (
    <Card className={cn(variant === "warning" && "border-amber-300 bg-amber-50/40")}>
      <CardHeader className="flex-row items-start gap-3 space-y-0 border-b-0 pb-2">
        <Icon className={cn("size-5 shrink-0", variant === "warning" ? "text-amber-600" : "text-foreground")} />
        <div className="min-w-0 flex-1">
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription className="mt-0.5">{description}</CardDescription>}
        </div>
      </CardHeader>
      {details && <CardContent className="py-2 text-xs">{details}</CardContent>}
      <CardContent className="flex items-center gap-2 pt-2">
        <Button size="sm" onClick={() => void onPrimary()} disabled={loading}>
          {loading ? "A processar..." : primaryLabel}
        </Button>
        {secondaryLabel && onSecondary && (
          <Button size="sm" variant="outline" onClick={() => void onSecondary()} disabled={loading}>
            {secondaryLabel}
          </Button>
        )}
        {(onBack || onSkip) && (
          <div className="ml-auto flex items-center gap-1">
            {onBack && (
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => void onBack()}
                disabled={loading || backLoading}
              >
                {backLoading ? "A voltar…" : backLabel}
              </Button>
            )}
            {onSkip && (
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => void onSkip()}
                disabled={loading || skipLoading}
              >
                {skipLoading ? "A saltar…" : skipLabel}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
